<template>
  <div
    class="user-avatar-input w-[100px] h-[100px] relative shrink-0"
    :title="$t('mainAside.youCanUploadImageByClick')"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div
      class="w-full h-full rounded-full overflow-hidden bg-bg-base-bg4 cursor-pointer"
    >
      <img
        v-if="file || isOldAvatarVisible"
        data-test="avatar-image"
        :src="imageHref || (oldAvatarUrl!)"
        class="w-full h-full object-cover"
        @click="openContextMenu"
      >

      <div
        v-else
        data-test="avatar-placeholder"
        class="w-full h-full flex justify-center items-center"
        @click="openFile"
      >
        <wui-icon
          name="cameraM"
          class="w-[48px]! h-[48px]! text-fg-base-fg22"
        />
        <div class="absolute -bottom-2 -right-2 p-2 rounded-10 bg-surf-neut-soft-def hover:bg-surf-neut-soft-hov active:bg-surf-neut-soft-pres">
          <wui-icon
            name="plusM"
            class="text-surf-brand-base-def"
          />
        </div>
      </div>
    </div>

    <div
      v-if="(file || isOldAvatarVisible) && isContextMenuOpened"
      v-click-outside="closeContextMenu"
      data-test="context-menu"
      class="absolute top-0 left-[94px] z-10"
    >
      <context-menu
        @view-avatar="viewAvatar"
        @remove-avatar="removeAvatar"
        @upload-file="openFileByContextMenu"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { WuiIcon, ClickOutside as vClickOutside, useRenderNotifier } from '@wui/common-library'
import { ref, computed } from 'vue'

import { useLocalization } from '@/shared/composables/localization'
import { usePasteImageOnHover } from '@/shared/composables/use-paste-image-on-hover'
import { MAX_AVATAR_SIZE_IN_BYTES } from '@/shared/composables/web-dav'
import { openImageDialog } from '@/shared/lib/image-dialog-bridge'

import ContextMenu from './context-menu.vue'

const props = defineProps<{
  oldAvatarUrl?: string
}>()

const emit = defineEmits<{
  (e: 'remove-old-avatar'): void
}>()

const file = defineModel<File | null>()

const { notifyError } = useRenderNotifier()
const { t } = useLocalization()

const allowedMimeTypes = ['image/jpeg', 'image/gif', 'image/png', 'image/bmp', 'image/dng', 'image/tiff']

const setAvatarFile = (f?: File) => {
  if (!f)
    return

  if (allowedMimeTypes.includes(f.type.toLowerCase())) {
    if (f.size > MAX_AVATAR_SIZE_IN_BYTES) {
      notifyError(t('common.attachedFileTooLargeDescription', { size: `${MAX_AVATAR_SIZE_IN_BYTES / 1e6} ${t('common.mb')}` }))
      return
    }

    file.value = f
    return
  }

  notifyError(t('mainAside.failedToLoadContactAvatarBecauseOfExtension'))
}

const openFile = () => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/*'
  input.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLInputElement
    setAvatarFile(target.files?.[0])
  }, { once: true })
  input.click()
}

const { onMouseEnter, onMouseLeave } = usePasteImageOnHover({
  onImagePaste: (pastedFile) => {
    setAvatarFile(pastedFile)
  },
})

const imageHref = computed(() => {
  if (!file.value) return ''

  return URL.createObjectURL(file.value)
})

const isContextMenuOpened = ref(false)
const openContextMenu = () => {
  isContextMenuOpened.value = true
}

const closeContextMenu = () => {
  isContextMenuOpened.value = false
}

const isOldAvatarRemoved = ref(false)
const isOldAvatarVisible = computed(() => {
  return !isOldAvatarRemoved.value && !!props.oldAvatarUrl
})

const removeAvatar = () => {
  file.value = null
  if (props.oldAvatarUrl) {
    isOldAvatarRemoved.value = true
    emit('remove-old-avatar')
  }
  closeContextMenu()
}

const openFileByContextMenu = () => {
  openFile()
  closeContextMenu()
}

const viewAvatar = () => {
  const src = imageHref.value ? imageHref.value : props.oldAvatarUrl
  void openImageDialog({
    src: src!,
    name: file.value?.name ?? '',
  })
  closeContextMenu()
}
</script>
